import type { Product } from '../../../../../../models/product/types';
import { normalizeSectionNumber } from '../AIAMaterial/AIAMaterialPledge.types';
import {
    LeedMepSectionRule,
    LeedMepClassification,
    MEPCategoryKey,
    MepProductClassification,
} from './LEEDv5MEP.types';

export const MEP_CATEGORY_ORDER: MEPCategoryKey[] = ['electrical', 'mechanical', 'plumbing'];

export interface MepTradeBucket {
    categoryId: MEPCategoryKey;
    isGeneral: boolean;
    isTargeted: boolean;
}

function sortMepCategories(categories: Iterable<MEPCategoryKey>): MEPCategoryKey[] {
    const set = new Set(categories);
    return MEP_CATEGORY_ORDER.filter((id) => set.has(id));
}

/**
 * Last-resort trade assignment when no accordion rule matches.
 * Only divisions with explicit General wildcards in the accordion (25/26/27/48 electrical,
 * 21/23/28 mechanical). Plumbing has no division-wide wildcard — only listed sections apply.
 */
function divisionFallbackCategories(normalized: string): MEPCategoryKey[] {
    const firstTwoDigits = normalized.split(/[\s.]/)[0];
    if (
        firstTwoDigits === '26' ||
        firstTwoDigits === '25' ||
        firstTwoDigits === '27' ||
        firstTwoDigits === '48'
    ) {
        return ['electrical'];
    }
    if (firstTwoDigits === '23' || firstTwoDigits === '21' || firstTwoDigits === '28') {
        return ['mechanical'];
    }
    return [];
}

export function classifyProduct(p: Product): MepProductClassification {
    const section = resolveMepMasterFormatSection(p);
    if (!section) {
        return {
            categories: [],
            isGeneral: false,
            isTargeted: false,
        };
    }

    const normalized = normalizeSectionNumber(section);

    const generalRules: LeedMepSectionRule[] = [];
    const targetedRules: LeedMepSectionRule[] = [];

    for (const group of LEED_MEP_SECTION_RULES) {
        if (group.classification === 'general') {
            generalRules.push(...group.rules);
        } else if (group.classification === 'targeted') {
            targetedRules.push(...group.rules);
        }
    }

    const matchingRules = [...generalRules, ...targetedRules].filter((rule) =>
        matchesRule(normalized, rule),
    );

    let categories = sortMepCategories(matchingRules.map((rule) => rule.categoryId));
    const usedDivisionFallback = categories.length === 0;
    if (usedDivisionFallback) {
        categories = divisionFallbackCategories(normalized);
    }

    const isGeneral =
        generalRules.some((rule) => matchesRule(normalized, rule)) ||
        (usedDivisionFallback && categories.length > 0);
    const isTargeted = targetedRules.some((rule) => matchesRule(normalized, rule));

    return {
        categories,
        isGeneral,
        isTargeted,
    };
}

/** General / Targeted flags for one trade table — OPTION column uses this, not global union. */
export function getMepEligibilityForCategory(
    p: Product,
    categoryId: MEPCategoryKey,
): { isGeneral: boolean; isTargeted: boolean } {
    const section = resolveMepMasterFormatSection(p);
    if (!section) {
        return { isGeneral: false, isTargeted: false };
    }
    return evaluateMepEligibilityForCategory(p, categoryId, section);
}

function evaluateMepEligibilityForCategory(
    p: Product,
    categoryId: MEPCategoryKey,
    section: string,
): { isGeneral: boolean; isTargeted: boolean } {
    const normalized = normalizeSectionNumber(section);
    const generalRules: LeedMepSectionRule[] = [];
    const targetedRules: LeedMepSectionRule[] = [];

    for (const group of LEED_MEP_SECTION_RULES) {
        if (group.classification === 'general') {
            generalRules.push(...group.rules);
        } else if (group.classification === 'targeted') {
            targetedRules.push(...group.rules);
        }
    }

    const matchingRules = [...generalRules, ...targetedRules].filter((rule) =>
        matchesRule(normalized, rule),
    );
    const usedDivisionFallback = matchingRules.length === 0;
    const fallbackCategories = usedDivisionFallback ? divisionFallbackCategories(normalized) : [];

    const inCategory =
        matchingRules.some((rule) => rule.categoryId === categoryId) ||
        fallbackCategories.includes(categoryId);
    if (!inCategory) {
        return { isGeneral: false, isTargeted: false };
    }

    const isGeneralByRule = generalRules.some(
        (rule) => rule.categoryId === categoryId && matchesRule(normalized, rule),
    );
    const isTargeted = targetedRules.some(
        (rule) => rule.categoryId === categoryId && matchesRule(normalized, rule),
    );

    return {
        isGeneral:
            isGeneralByRule || (usedDivisionFallback && fallbackCategories.includes(categoryId)),
        isTargeted,
    };
}

export interface MepRuleMatchSummary {
    classification: 'general' | 'targeted';
    categoryId: MEPCategoryKey;
    masterFormatSection: string;
    matchRule: string;
    rangeEnd?: string;
}

export interface MepProductClassificationExplanation extends MepProductClassification {
    mfSection: string | null;
    normalizedSection: string | null;
    /** Per-trade OPTION labels (matches table rows). */
    optionsByCategory: Partial<Record<MEPCategoryKey, string>>;
    optionLabel: string;
    matchedGeneralRules: MepRuleMatchSummary[];
    matchedTargetedRules: MepRuleMatchSummary[];
}

function collectMatchingRuleSummaries(
    normalized: string,
    rules: LeedMepSectionRule[],
    classification: 'general' | 'targeted',
): MepRuleMatchSummary[] {
    return rules
        .filter((rule) => matchesRule(normalized, rule))
        .map((rule) => ({
            classification,
            categoryId: rule.categoryId,
            masterFormatSection: rule.masterFormatSection,
            matchRule: rule.matchRule,
            rangeEnd: rule.rangeEnd,
        }));
}

/** Debug helper: shows which MF section and rules drove General / Targeted flags. */
export function explainMepProductClassification(p: Product): MepProductClassificationExplanation {
    const mfSection = resolveMepMasterFormatSection(p);
    if (!mfSection) {
        return {
            categories: [],
            isGeneral: false,
            isTargeted: false,
            mfSection: null,
            normalizedSection: null,
            optionsByCategory: {},
            optionLabel: '',
            matchedGeneralRules: [],
            matchedTargetedRules: [],
        };
    }

    const normalized = normalizeSectionNumber(mfSection);
    const generalRules: LeedMepSectionRule[] = [];
    const targetedRules: LeedMepSectionRule[] = [];

    for (const group of LEED_MEP_SECTION_RULES) {
        if (group.classification === 'general') {
            generalRules.push(...group.rules);
        } else if (group.classification === 'targeted') {
            targetedRules.push(...group.rules);
        }
    }

    const result = classifyProduct(p);
    const matchedGeneralRules = collectMatchingRuleSummaries(normalized, generalRules, 'general');
    const matchedTargetedRules = collectMatchingRuleSummaries(
        normalized,
        targetedRules,
        'targeted',
    );
    const optionsByCategory: Partial<Record<MEPCategoryKey, string>> = {};
    for (const categoryId of result.categories) {
        const { isGeneral, isTargeted } = getMepEligibilityForCategory(p, categoryId);
        const parts: string[] = [];
        if (isGeneral) {
            parts.push('General');
        }
        if (isTargeted) {
            parts.push('Targeted');
        }
        if (parts.length > 0) {
            optionsByCategory[categoryId] = parts.join(', ');
        }
    }
    const optionLabel = Object.entries(optionsByCategory)
        .map(([categoryId, label]) => `${categoryId}: ${label}`)
        .join('; ');

    return {
        ...result,
        mfSection,
        normalizedSection: normalized,
        optionsByCategory,
        optionLabel,
        matchedGeneralRules,
        matchedTargetedRules,
    };
}

/** Buckets project products by MEP Option column outcome (for QA / TL test planning). */
export function summarizeMepProductOptions(products: Product[]): {
    generalOnly: MepProductClassificationExplanation[];
    targetedOnly: MepProductClassificationExplanation[];
    both: MepProductClassificationExplanation[];
    notInMep: MepProductClassificationExplanation[];
} {
    const generalOnly: MepProductClassificationExplanation[] = [];
    const targetedOnly: MepProductClassificationExplanation[] = [];
    const both: MepProductClassificationExplanation[] = [];
    const notInMep: MepProductClassificationExplanation[] = [];

    for (const product of products) {
        const explanation = explainMepProductClassification(product);
        if (explanation.categories.length === 0) {
            notInMep.push(explanation);
            continue;
        }

        const rowFlags = explanation.categories.map((categoryId) =>
            getMepEligibilityForCategory(product, categoryId),
        );
        const eligibleRows = rowFlags.filter((flags) => flags.isGeneral || flags.isTargeted);
        if (eligibleRows.length === 0) {
            notInMep.push(explanation);
            continue;
        }

        const hasBothRow = eligibleRows.some((flags) => flags.isGeneral && flags.isTargeted);
        const hasGeneralRow = eligibleRows.some((flags) => flags.isGeneral);
        const hasTargetedRow = eligibleRows.some((flags) => flags.isTargeted);

        if (hasBothRow || (hasGeneralRow && hasTargetedRow)) {
            both.push(explanation);
        } else if (hasGeneralRow) {
            generalOnly.push(explanation);
        } else if (hasTargetedRow) {
            targetedOnly.push(explanation);
        } else {
            notInMep.push(explanation);
        }
    }

    return { generalOnly, targetedOnly, both, notInMep };
}

/** Returns true when the product matches any MEP category rule (mechanical/electrical/plumbing). */
export function isProductInAnyMepCategory(p: Product): boolean {
    return classifyProduct(p).categories.length > 0;
}

function matchesRule(section: string, rule: LeedMepSectionRule): boolean {
    const baseMatch = match(section, rule);
    if (!baseMatch) {
        return false;
    }
    if (rule.exclude?.length) {
        const excluded = rule.exclude.some((ex) => match(section, ex));
        if (excluded) {
            return false;
        }
    }

    return true;
}

function match(
    section: string,
    rule: {
        masterFormatSection: string;
        matchRule: 'startsWith' | 'exact' | 'range';
        rangeEnd?: string;
    },
): boolean {
    switch (rule.matchRule) {
        case 'exact':
            return (
                section === rule.masterFormatSection ||
                section.startsWith(rule.masterFormatSection + ' ') ||
                section.startsWith(rule.masterFormatSection + '.')
            );

        case 'startsWith':
            return section.startsWith(rule.masterFormatSection);

        case 'range':
            return isInRange(section, rule.masterFormatSection, rule.rangeEnd!);

        default:
            return false;
    }
}

function isInRange(value: string, start: string, end: string): boolean {
    const normalize = (s: string) =>
        s
            .split(/[.\s]+/)
            .map((n) => n.padStart(3, '0'))
            .join('');

    const v = Number(normalize(value));
    const sNum = Number(normalize(start));
    const eNum = Number(normalize(end));

    return v >= sNum && v <= eNum;
}

export const LEED_MEP_SECTION_RULES: readonly LeedMepClassification[] = [
    {
        classification: 'general',
        rules: [
            // Mechanical
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23',
                matchRule: 'startsWith',
                priority: 60,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '21',
                matchRule: 'startsWith',
                priority: 60,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '28',
                matchRule: 'startsWith',
                priority: 60,
            },

            // Electrical
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26',
                matchRule: 'startsWith',
                priority: 60,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '48',
                matchRule: 'startsWith',
                priority: 60,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '25',
                matchRule: 'startsWith',
                priority: 60,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '27 2',
                matchRule: 'startsWith',
                priority: 60,
            },

            // Plumbing
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '13 47 13.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 05 16',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 05 17',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 05 23',
                matchRule: 'range',
                rangeEnd: '21 05 53',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 11',
                matchRule: 'startsWith',
                priority: 70,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 21 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 22 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 23 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 24 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 10',
                matchRule: 'startsWith',
                priority: 70,
            },

            // Section 22 specifics
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 13 16',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 13 19',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 19',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 23',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 15 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 51 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 52 13',
                matchRule: 'exact',
                priority: 80,
            },

            // Range rules
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 61 13',
                matchRule: 'range',
                rangeEnd: '22 61 13.74',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 62 13',
                matchRule: 'range',
                rangeEnd: '22 62 13.74',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 62 23',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 63 13',
                matchRule: 'range',
                rangeEnd: '22 63 13.70',
                priority: 90,
            },

            // Misc plumbing mappings
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 66 53',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 66 70',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 67 13',
                matchRule: 'range',
                rangeEnd: '22 67 13.19',
                priority: 90,
            },

            // Section 23
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 11',
                matchRule: 'startsWith',
                priority: 70,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 21 13',
                matchRule: 'range',
                rangeEnd: '23 21 16',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 22 13',
                matchRule: 'range',
                rangeEnd: '23 22 16',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 23 00',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 24 00',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 83 16',
                matchRule: 'exact',
                priority: 80,
            },

            // Other systems
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '25 05 28.33',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '26 05 33.13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '27 05 33',
                matchRule: 'startsWith',
                priority: 70,
            },

            // Civil / site plumbing
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '31 31 33',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 05 07.24',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 14 11',
                matchRule: 'range',
                rangeEnd: '33 14 16',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 11',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 19',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 23',
                matchRule: 'range',
                rangeEnd: '33 31 29',
                priority: 90,
            },

            // Drainage / utilities
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 13.13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 16',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 19.13',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 11',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 16',
                matchRule: 'exact',
                priority: 80,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 23',
                matchRule: 'range',
                rangeEnd: '33 42 26.23',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 52 13',
                matchRule: 'range',
                rangeEnd: '33 52 16.26',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 63 13',
                matchRule: 'range',
                rangeEnd: '33 63 23',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22',
                matchRule: 'startsWith',
                priority: 75,

                exclude: [
                    { masterFormatSection: '22 4', matchRule: 'startsWith' },
                    { masterFormatSection: '22 5', matchRule: 'startsWith' },
                    { masterFormatSection: '22 6', matchRule: 'startsWith' },
                ],
            },
        ],
    },
    {
        classification: 'targeted',
        rules: [
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 20',
                matchRule: 'startsWith',
                priority: 90,
            },
            // ✅ Terminal Units
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '26 36',
                matchRule: 'startsWith',
                priority: 90,
            },

            // ✅ Air Terminals
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 06 30.16',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Boilers
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 52',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 53',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '42 11',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '46 78 47',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '48 11 13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Heat Exchangers
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '22 35',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 57',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '42 13',
                matchRule: 'startsWith',
                priority: 90,
            },

            // ✅ Air Handling Units
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 06 70.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 73',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 74 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 75 13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Ductwork
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 31',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '23 33',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '26 05 43',
                matchRule: 'exact',
                priority: 95,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '27 05 43',
                matchRule: 'exact',
                priority: 95,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '33 05 31.29',
                matchRule: 'exact',
                priority: 95,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '33 05 33.63',
                matchRule: 'exact',
                priority: 95,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '33 71 19',
                matchRule: 'exact',
                priority: 95,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '33 71 19.23',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'mechanical',
                categoryLabel: 'Mechanical',
                masterFormatSection: '33 71 19.33',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Wiring
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 71 36',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 71 39',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 71 49',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 72 43',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Conduit
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 05 33.13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Generators
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 32 00',
                matchRule: 'range',
                rangeEnd: '26 32 19',
                priority: 100,
            },

            // ✅ Panelboards + Switchboards (collapsed overlap)
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 06 20.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 06 50.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 09 26',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 24 16',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 24 16.16',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 24 13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Transformers
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 12',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 22',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 71 26.23',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 71 26.26',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '33 73',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '48 19 23',
                matchRule: 'exact',
                priority: 100,
            },
            // ✅ Cabletray
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '25 05 28.36',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '26 05 36',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'electrical',
                categoryLabel: 'Electrical',
                masterFormatSection: '27 05 36',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Fire / plumbing related (21)
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '13 47 13.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 22 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 23 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '21 24 13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Division 22 core
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 10',
                matchRule: 'startsWith',
                priority: 90,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 13 16',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 13 19',
                matchRule: 'exact',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 19',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 14 23',
                matchRule: 'exact',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 15 13',
                matchRule: 'exact',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 51 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 52 13',
                matchRule: 'exact',
                priority: 100,
            },

            // ✅ Ranges
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 61 13',
                matchRule: 'range',
                rangeEnd: '22 61 13.74',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 62 13',
                matchRule: 'range',
                rangeEnd: '22 62 13.74',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 62 23',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 63 13',
                matchRule: 'range',
                rangeEnd: '22 63 13.70',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 66 53',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 66 70',
                matchRule: 'exact',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '22 67 13',
                matchRule: 'range',
                rangeEnd: '22 67 13.19',
                priority: 100,
            },

            // ✅ Division 23 overlaps
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 11',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 21 13',
                matchRule: 'range',
                rangeEnd: '23 21 16',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 22 13',
                matchRule: 'range',
                rangeEnd: '23 22 16',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 23 00',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 24 00',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '23 83 16',
                matchRule: 'exact',
                priority: 100,
            },
            // ✅ Cross divisions
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '25 05 28.33',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '26 05 33.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '27 05 33',
                matchRule: 'startsWith',
                priority: 90,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '31 31 33',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 05 07.24',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 14 11',
                matchRule: 'range',
                rangeEnd: '33 14 16',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 11',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 19',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 31 23',
                matchRule: 'range',
                rangeEnd: '33 31 29',
                priority: 100,
            },

            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 13.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 16',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 41 19.13',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 11',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 16',
                matchRule: 'exact',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 42 23',
                matchRule: 'range',
                rangeEnd: '33 42 26.23',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 52 13',
                matchRule: 'range',
                rangeEnd: '33 52 16.26',
                priority: 100,
            },
            {
                categoryId: 'plumbing',
                categoryLabel: 'Plumbing',
                masterFormatSection: '33 63 13',
                matchRule: 'range',
                rangeEnd: '33 63 23',
                priority: 100,
            },
        ],
    },
] as const;

export interface LeedMepCategoryRule {
    readonly id: MEPCategoryKey;
    readonly label: string;
}

export const LEED_MEP_CATEGORY_RULES: readonly LeedMepCategoryRule[] = [
    { id: 'electrical', label: 'Electrical' },
    { id: 'mechanical', label: 'Mechanical' },
    { id: 'plumbing', label: 'Plumbing' },
];

/** Resolved MF section used for MEP classification (alias for pipeline debug tooling). */
export function getProductMfSectionNumber(p: Product): string | null {
    return resolveMepMasterFormatSection(p);
}

/** Sets lowestLevelNumber/Label from accordion-aligned MF resolution (Products + Reporting tabs). */
export function normalizeProductMasterFormat(p: Product): Product {
    const resolved = resolveMepMasterFormatSection(p);
    if (!resolved) {
        return p;
    }
    const label = mfLabelForNumber(p, resolved);
    return {
        ...p,
        lowestLevelNumber: resolved,
        ...(label ? { lowestLevelLabel: label } : {}),
    };
}

/** Every distinct MF number stored on a product (filed subgroup before finest taxonomy). */
export function getMepMasterFormatCandidates(p: Product): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of [
        p.lowestLevelNumber,
        p.mfSectionLevel2?.number,
        p.mfSectionLevel1?.number,
        p.mfSection?.number,
        p.mfSectionLevel3?.number,
    ]) {
        if (raw == null) {
            continue;
        }
        const trimmed = String(raw).trim();
        if (!trimmed || seen.has(trimmed)) {
            continue;
        }
        seen.add(trimmed);
        result.push(trimmed);
    }
    return result;
}

function countMepTradeBucketsForSection(p: Product, mfSection: string): number {
    let count = 0;
    for (const categoryId of MEP_CATEGORY_ORDER) {
        const { isGeneral, isTargeted } = evaluateMepEligibilityForCategory(
            p,
            categoryId,
            mfSection,
        );
        if (isGeneral || isTargeted) {
            count++;
        }
    }
    return count;
}

/** Picks the MF number that best matches the LEED MEP accordion for this product. */
export function resolveMepMasterFormatSection(p: Product): string | null {
    const level2 = p.mfSectionLevel2?.number?.trim();
    const level3 = p.mfSectionLevel3?.number?.trim();
    if (level2 && level3 && level2 !== level3) {
        const level2Count = countMepTradeBucketsForSection(p, level2);
        const level3Count = countMepTradeBucketsForSection(p, level3);
        if (level2Count < level3Count) {
            return level2;
        }
        if (level3Count < level2Count) {
            return level3;
        }
    }

    const candidates = getMepMasterFormatCandidates(p);
    if (candidates.length === 0) {
        return null;
    }
    if (candidates.length === 1) {
        return candidates[0];
    }

    let best = candidates[0];
    let bestCount = countMepTradeBucketsForSection(p, best);
    for (let i = 1; i < candidates.length; i++) {
        const candidate = candidates[i];
        const count = countMepTradeBucketsForSection(p, candidate);
        if (
            count < bestCount ||
            (count === bestCount && candidate.localeCompare(best, undefined, { numeric: true }) < 0)
        ) {
            best = candidate;
            bestCount = count;
        }
    }
    return best;
}

export function mfLabelForNumber(p: Product, number: string): string | undefined {
    if (p.mfSectionLevel3?.number === number) {
        return p.mfSectionLevel3.label;
    }
    if (p.mfSectionLevel2?.number === number) {
        return p.mfSectionLevel2.label;
    }
    if (p.mfSectionLevel1?.number === number) {
        return p.mfSectionLevel1.label;
    }
    if (p.mfSection?.number === number) {
        return p.mfSection.label;
    }
    return p.lowestLevelLabel;
}

/** Trade tables and per-row General/Targeted flags for the MEP product table. */
export function getMepTradeBuckets(p: Product): MepTradeBucket[] {
    const section = resolveMepMasterFormatSection(p);
    if (!section) {
        return [];
    }

    const buckets: MepTradeBucket[] = [];
    for (const categoryId of MEP_CATEGORY_ORDER) {
        const { isGeneral, isTargeted } = evaluateMepEligibilityForCategory(p, categoryId, section);
        if (isGeneral || isTargeted) {
            buckets.push({ categoryId, isGeneral, isTargeted });
        }
    }
    return buckets;
}
